import { BRAND, HEADER_CSS, esc, icon, page, traceLine } from './base.js'

/**
 * ใบเสร็จรับเงิน / Receipt — HTML replacement for the Google Docs template.
 *
 * Input is EXACTLY what buildReceiptPayload() in DocumentGenerator.jsx already
 * produces for the Apps Script renderer — the grouping, the doctor-fee row, the
 * Thai baht text and the next-appointment object are all computed there. This
 * file only lays them out, so moving a document off Apps Script changes no
 * money logic.
 *
 * `lang: 'en'` reads the *EN fields of the same payload (the receipt_en doc).
 *
 * `kind` renders the sibling billing documents from the same layout:
 *   'invoice'    — ใบแจ้งหนี้, from buildReceiptPayload() (same payload as the
 *                  receipt); invoice wording + issuing staff and issue date.
 *   'fee_detail' — เอกสารแสดงรายละเอียดค่ารักษาพยาบาล, from
 *                  buildFeeDetailPayload(); no next-appointment box.
 *
 * Deliberately NO signature line on the receipt: receipts are often emailed,
 * and a blank line no one can sign makes an emailed receipt look unfinished
 * (Ram, 2026-10-15). The receiving staff member is printed by name instead.
 * The invoice is the exception — it is printed and signed by the patient at
 * the counter, so it carries a patient signature box (Ram, 2026-10-23).
 */

const L = {
  th: {
    title: 'ใบเสร็จรับเงิน', sub: 'Receipt',
    ref: 'เลขที่อ้างอิง', date: 'วันที่รับบริการ',
    patient: 'ข้อมูลผู้ป่วย', hn: 'HN', addr: 'ที่อยู่', doctor: 'ทันตแพทย์ผู้รักษา', lic: 'ท.',
    issuer: 'ผู้ออกใบเสร็จ', tax: 'เลขประจำตัวผู้เสียภาษี', branchLic: 'ใบอนุญาตประกอบกิจการสถานพยาบาล', sso: 'รหัสสถานพยาบาล',
    no: 'ลำดับ', item: 'รายการค่ารักษาพยาบาล', amount: 'จำนวนเงิน (บาท)',
    total: 'รวมทั้งสิ้น', received: 'ผู้รับเงิน',
    next: 'นัดหมายครั้งถัดไป',
    clinic: 'คลินิกทันตกรรมสไมล์ซีซันส์ สาขา',
  },
  en: {
    title: 'Receipt', sub: 'ใบเสร็จรับเงิน',
    ref: 'Reference no.', date: 'Date of service',
    patient: 'Patient', hn: 'HN', addr: 'Address', doctor: 'Attending dentist', lic: 'License',
    issuer: 'Issued by', tax: 'Tax ID', branchLic: 'Healthcare facility license', sso: 'Facility code',
    no: 'No.', item: 'Description', amount: 'Amount (THB)',
    total: 'Total', received: 'Received by',
    next: 'Next appointment',
    clinic: 'Smile Seasons Dental Clinic · ',
  },
}

// The sibling documents: wording (from their Google Docs templates) and which
// blocks they carry. `long` = a title too long for receipt size.
const KINDS = {
  receipt: { next: true, issued: false, th: {}, en: {} },
  invoice: {
    next: true, issued: true, sign: true,
    th: {
      title: 'ใบแจ้งหนี้', sub: 'Invoice',
      ref: 'หมายเลขอ้างอิง / เลขที่ใบเสร็จ', issuer: 'ผู้ออกเอกสาร', amount: 'ราคา (บาท)',
      total: 'ค่ารักษาพยาบาลรวม', received: 'ผู้ทำรายการ', issued: 'วันที่ออกรายงาน',
      next: 'รายการนัดต่อเนื่อง', sign: 'ผู้รับบริการ',
    },
    en: {
      title: 'Invoice', sub: 'ใบแจ้งหนี้',
      ref: 'Reference no. / Receipt no.', issuer: 'Healthcare provider', item: 'Treatment items',
      total: 'Total treatment fee', received: 'Issued by', issued: 'Date issued',
      sign: 'Patient',
    },
  },
  fee_detail: {
    next: false, issued: true, long: true,
    th: {
      title: 'เอกสารแสดงรายละเอียดค่ารักษาพยาบาล', sub: 'Statement of Medical Expenses',
      ref: 'หมายเลขอ้างอิง / เลขที่ใบเสร็จ', issuer: 'ผู้ออกเอกสาร', amount: 'ราคา (บาท)',
      total: 'ค่ารักษาพยาบาลรวม', received: 'ผู้ทำรายการ', issued: 'วันที่ออกรายงาน',
    },
    en: {
      title: 'Statement of Medical Expenses', sub: 'เอกสารแสดงรายละเอียดค่ารักษาพยาบาล',
      ref: 'Reference no. / Receipt no.', issuer: 'Healthcare provider', item: 'Treatment items',
      total: 'Total treatment fee', received: 'Issued by', issued: 'Date issued',
    },
  },
}

export function renderReceipt(d, { lang = 'th', assets = {}, kind = 'receipt' } = {}) {
  const k = KINDS[kind] || KINDS.receipt
  const t = { ...(L[lang] || L.th), ...(k[lang] || k.th) }
  const en = lang === 'en'
  const pick = (th, enKey) => (en && d[enKey] != null && d[enKey] !== '' ? d[enKey] : d[th])

  const items = Array.isArray(d.treatments) ? d.treatments : []
  const rows = items.map((it) => `
      <tr>
        <td class="c-no num">${esc(it.no)}</td>
        <td class="c-item">${esc(en ? (it.treatment_listEN ?? it.treatment_list) : it.treatment_list)}</td>
        <td class="c-amt num">${esc(en ? (it.treatment_priceEN ?? it.treatment_price) : it.treatment_price)}</td>
      </tr>`).join('')

  const na = k.next ? (en ? d.next_appointmentEN : d.next_appointment) : null
  const nextBlock = na && typeof na === 'object' ? (na.has
    ? (en
      ? `<b>${esc(na.date)}</b> at <b>${esc(na.time)}</b><br>${esc(na.branch_name)} · ${esc(na.doctor_name)}`
      : `<b>${esc(na.date)}</b> เวลา <b>${esc(na.time)} น.</b><br>สาขา${esc(na.branch_name)} · ทันตแพทย์ ${esc(na.doctor_name)}`)
    : (en
      ? 'No upcoming appointment — please contact our staff to book your next visit.'
      : 'ยังไม่มีนัดหมายครั้งถัดไป — ติดต่อเจ้าหน้าที่เพื่อนัดหมาย และดูแลสุขภาพช่องปากอย่างต่อเนื่อง'))
    : ''

  const branch = pick('branch_name', 'branch_nameEN')

  const body = `
  <header class="top">
    <div>
      <h1${k.long ? ' class="long"' : ''}>${t.title}</h1>
      <div class="sub">${t.sub}</div>
    </div>
    ${assets.logo ? `<img class="logo" src="${esc(assets.logo)}" alt="Smile Seasons Dental Clinic">` : ''}
  </header>

  <section class="meta">
    <div><span>${t.ref}</span><b class="num">${esc(d.ref_num)}</b></div>
    <div><span>${t.date}</span><b>${esc(pick('service_date', 'service_dateEN') || '-')}</b></div>
  </section>

  <section class="parties">
    <div class="party">
      <div class="label">${t.patient}</div>
      <div class="name">${esc(pick('fullname', 'fullnameEN'))}</div>
      <div class="kv"><span>${t.hn}</span>${esc(d.HN)}</div>
      ${d.address ? `<div class="kv"><span>${t.addr}</span>${esc(d.address)}</div>` : ''}
      <div class="kv"><span>${t.doctor}</span>${esc(pick('doctor_fullname', 'doctor_fullnameEN'))}${d.doctor_license ? ` <span class="muted">(${t.lic} ${esc(d.doctor_license)})</span>` : ''}</div>
    </div>
    <div class="party">
      <div class="label">${t.issuer}</div>
      <div class="name">${esc(pick('company_name', 'company_nameEN'))}</div>
      ${pick('company_address', 'company_addressEN') ? `<div class="plain">${esc(pick('company_address', 'company_addressEN'))}</div>` : ''}
      ${d.company_taxnumber ? `<div class="kv"><span>${t.tax}</span><span class="num">${esc(d.company_taxnumber)}</span></div>` : ''}
      ${d.branch_license_number ? `<div class="kv"><span>${t.branchLic}</span><span class="num">${esc(d.branch_license_number)}</span></div>` : ''}
      ${d.branch_ssonumber ? `<div class="kv"><span>${t.sso}</span><span class="num">${esc(d.branch_ssonumber)}</span></div>` : ''}
    </div>
  </section>

  <table class="items">
    <thead><tr><th class="c-no">${t.no}</th><th class="c-item">${t.item}</th><th class="c-amt">${t.amount}</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <section class="total">
    <div class="words">${esc(en ? (d.treatment_price_totalEN || '') : (d.treatment_price_totalThai ? `(${d.treatment_price_totalThai})` : ''))}</div>
    <div class="sum"><span>${t.total}</span><b class="num">${esc(d.treatment_price_total)}</b><i>${en ? 'THB' : 'บาท'}</i></div>
  </section>

  <section class="bottom">
    ${nextBlock ? `<div class="next"><div class="label">${icon('calendar', 12)} ${t.next}</div><div>${nextBlock}</div></div>` : '<div></div>'}
    <div class="received">
      <span class="muted">${t.received}</span>
      <b>${esc(pick('employee_fullname', 'employee_fullnameEN'))}</b>
      ${k.issued && pick('current_date', 'current_dateEN') ? `<span class="muted issued">${t.issued} ${esc(pick('current_date', 'current_dateEN'))}</span>` : ''}
    </div>
  </section>${k.sign ? `

  <section class="sign">
    <div class="sign-box">
      <div class="sign-line"></div>
      <b>(${esc(pick('fullname', 'fullnameEN'))})</b>
      <span>${t.sign}</span>
    </div>
  </section>` : ''}`

  return page({
    title: `${t.title} ${d.ref_num || ''}`.trim(),
    lang,
    assets,
    footer: `${t.clinic}${branch || ''}`,
    meta: traceLine(d, lang),
    body,
    css: CSS,
  })
}

const CSS = `${HEADER_CSS}
table.items { width: 100%; border-collapse: collapse; }
table.items thead { display: table-header-group; }   /* repeat on every page */
table.items tr { break-inside: avoid; }
table.items th { background: ${BRAND.blue}; color: #fff; font-weight: 600; font-size: 9pt; text-align: left; padding: 2.2mm 3mm; }
table.items td { padding: 1.7mm 3mm; border-bottom: 1px solid ${BRAND.rule}; vertical-align: top; }
table.items tbody tr:nth-child(even) td { background: #f8fafc; }
.c-no { width: 13mm; text-align: center !important; color: ${BRAND.muted}; }
.c-amt { width: 36mm; text-align: right !important; }

.total { display: flex; justify-content: space-between; align-items: center; gap: 6mm; margin-top: 0; padding: 3.2mm 3mm; background: ${BRAND.blueSoft}; border-bottom: 2px solid ${BRAND.blue}; break-inside: avoid; }
.total .words { font-size: 9.5pt; color: #334155; }
.total .sum { display: flex; align-items: baseline; gap: 3mm; white-space: nowrap; }
.total .sum span { font-size: 9.5pt; font-weight: 600; color: ${BRAND.blue}; }
.total .sum b { font-family: 'Plex Thai', sans-serif; font-weight: 600; font-size: 14pt; color: ${BRAND.ink}; }
.total .sum i { font-style: normal; font-size: 9pt; color: ${BRAND.muted}; }

.bottom { display: grid; grid-template-columns: 1fr 62mm; gap: 10mm; margin-top: 10mm; align-items: end; break-inside: avoid; }
.next { border: 1px solid ${BRAND.blueLine}; border-radius: 2.5mm; padding: 3mm 4mm; font-size: 9.5pt; background: #fbfdff; }
.next .label { display: flex; align-items: center; gap: 1.5mm; }
.received { text-align: right; font-size: 9.5pt; display: flex; flex-direction: column; }
.top h1.long { font-size: 17pt; white-space: nowrap; }   /* the fee-detail title, one line beside the logo */
.received b { font-weight: 600; }
.received .issued { margin-top: 1mm; font-size: 8.5pt; }

/* Invoice only: the patient signs at the counter */
.sign { display: flex; justify-content: flex-end; margin-top: 8mm; break-inside: avoid; }
.sign-box { width: 62mm; display: flex; flex-direction: column; align-items: center; text-align: center; font-size: 9.5pt; }
.sign-line { width: 100%; height: 14mm; border-bottom: 1px dotted #94a3b8; margin-bottom: 2mm; }
.sign-box b { font-weight: 600; font-size: 10.5pt; }
.sign-box span { color: ${BRAND.muted}; }
`
